import { useId, useState, type InputHTMLAttributes } from 'react'

type PasswordFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: string
  /** Extra classes on the outer label wrapper. */
  wrapperClassName?: string
  /** Extra classes on the input (defaults match sign-in forms). */
  inputClassName?: string
}

function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    // "Hide password" — eye with slash
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
        aria-hidden="true"
      >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    )
  }
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

/**
 * Accessible password input with a show/hide toggle (eye button).
 * Toggle is keyboard-focusable and announces state via aria-pressed / aria-label.
 */
export function PasswordField({
  label,
  id: idProp,
  wrapperClassName,
  inputClassName,
  className,
  disabled,
  ...inputProps
}: PasswordFieldProps) {
  const generatedId = useId()
  const id = idProp ?? generatedId
  const [visible, setVisible] = useState(false)

  return (
    <label htmlFor={id} className={wrapperClassName ?? 'block text-sm font-medium text-slate-700'}>
      {label}
      <span className="relative mt-1 block">
        <input
          {...inputProps}
          id={id}
          type={visible ? 'text' : 'password'}
          disabled={disabled}
          className={
            inputClassName ??
            className ??
            'w-full rounded-xl border border-slate-200 py-2.5 pl-3 pr-11 text-sm outline-none ring-brand-500 focus:ring-2 disabled:opacity-50'
          }
        />
        <button
          type="button"
          tabIndex={0}
          disabled={disabled}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          aria-controls={id}
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 transition hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 disabled:opacity-50"
        >
          <EyeIcon open={visible} />
        </button>
      </span>
    </label>
  )
}
